import { lazy } from 'react';
import { IFrontPermissionNode, IRouter } from '@/@types/router.d';
import { FunctionListModule } from '@/utils/router';

const PARENT_SECONDARY_MAP: Record<string, string[]> = {
  '/home': [],
  '/generalTradeLogistics/salesOrders': [
    '/generalTradeLogistics/purchaseOrders',
    '/generalTradeLogistics/salesOrders/detail',
    '/generalTradeLogistics/salesOrders/fo-detail',
    '/generalTradeLogistics/salesOrders/po-detail',
    '/generalTradeLogistics/salesOrders/to-detail',
  ],
  '/generalTradeLogisticsBase/providerConfig': ['/generalTradeLogistics/providerConfig'],
  '/generalTradeLogisticsBase/systemConfig': ['/generalTradeLogistics/systemConfig'],
  '/generalTradeLogisticsBase/financeFoundation': [],
  '/accountCenter/account': ['/accountCenter/account/edit', '/accountCenter/account/detail'],
  '/accountCenter/role': ['/accountCenter/role/edit', '/accountCenter/role/detail'],
};

const createPermissionNode = (
  frontPath: string,
  frontTitle: string,
  permissionCode: string,
  extraPermissions: Array<{ code: string; name: string }> = [],
): IFrontPermissionNode => ({
  id: null,
  createDate: null,
  updateDate: null,
  del: false,
  frontPath,
  frontTitle,
  frontRelationPermission: null,
  level: 2,
  version: 1,
  publishDate: 0,
  permissionList: [
    { backCode: `${permissionCode}.READ`, backDesc: '查看', frontCode: 'read', frontName: '查看' },
    ...extraPermissions.map(({ code, name }) => ({
      backCode: `${permissionCode}.${code.replace(/[A-Z]/g, (value) => `_${value}`).toUpperCase()}`,
      backDesc: name,
      frontCode: code,
      frontName: name,
    })),
  ],
  children: [],
});

const createPermissionGroup = (
  frontPath: string,
  frontTitle: string,
  children: IFrontPermissionNode[],
): IFrontPermissionNode => ({
  id: null,
  createDate: null,
  updateDate: null,
  del: false,
  frontPath,
  frontTitle,
  frontRelationPermission: null,
  level: 1,
  version: 1,
  publishDate: 0,
  permissionList: [],
  children,
});

const FrontPermissionTree: IFrontPermissionNode[] = [
  createPermissionGroup('/generalTradeLogisticsFulfillment', '头程履约', [
    createPermissionNode('/generalTradeLogistics/fulfillmentWorkbench', '履约工作台', 'PERMISSION_LOGISTICS_FULFILLMENT_WORKBENCH'),
  ]),
  createPermissionGroup('/generalTradeLogistics', '头程竞价管理', [
    createPermissionNode('/generalTradeLogistics/freightInquiry', '发起询价', 'PERMISSION_LOGISTICS_FREIGHT_INQUIRY'),
    createPermissionNode('/generalTradeLogistics/inquiryManagement', '询价管理', 'PERMISSION_LOGISTICS_INQUIRY_MANAGEMENT'),
    createPermissionNode('/generalTradeLogistics/competitionAnalysis', '竞价分析', 'PERMISSION_LOGISTICS_COMPETITION_ANALYSIS'),
    createPermissionNode('/generalTradeLogistics/pricingReview', '定价审核', 'PERMISSION_LOGISTICS_PRICING_REVIEW'),
    createPermissionNode('/generalTradeLogistics/logisticsTracking', '物流轨迹跟踪', 'PERMISSION_LOGISTICS_TRACKING'),
    createPermissionNode('/generalTradeLogistics/manualTracking', '头程物流录入', 'PERMISSION_LOGISTICS_MANUAL_TRACKING'),
    createPermissionNode('/generalTradeLogistics/supplytrack', '第三方物流轨迹查询', 'PERMISSION_LOGISTICS_SUPPLY_TRACK'),
    createPermissionNode('/generalTradeLogistics/reconciliationReview', '对账与审核', 'PERMISSION_LOGISTICS_RECONCILIATION_REVIEW'),
    createPermissionNode('/generalTradeLogistics/exceptionPermission', '异常与权限', 'PERMISSION_LOGISTICS_EXCEPTION_PERMISSION'),
  ]),
  createPermissionGroup('/generalTradeLogisticsOrders', 'AI财务', [
    createPermissionNode('/generalTradeLogistics/salesOrders', '单据池', 'PERMISSION_LOGISTICS_SALES_ORDERS'),
  ]),
  createPermissionGroup('/generalTradeLogisticsBase', '基础配置', [
    createPermissionNode('/generalTradeLogisticsBase/providerConfig', '物流商参数配置', 'PERMISSION_LOGISTICS_PROVIDER_CONFIG'),
    createPermissionNode('/generalTradeLogisticsBase/systemConfig', '系统参数配置', 'PERMISSION_LOGISTICS_SYSTEM_CONFIG'),
    createPermissionNode('/generalTradeLogisticsBase/financeFoundation', '财务基础配置', 'PERMISSION_FINANCE_FOUNDATION', [
      { code: 'edit', name: '编辑' },
      { code: 'enable', name: '启用/停用' },
    ]),
    createPermissionNode('/generalTradeLogisticsBase/lingxingProducts', '领星产品', 'PERMISSION_LOGISTICS_LINGXING_PRODUCTS'),
  ]),
  createPermissionGroup('/accountCenter', '用户权限管理', [
    createPermissionNode('/accountCenter/account', '后台用户管理', 'PERMISSION_ACCOUNT_USER', [
      { code: 'detail', name: '详情' },
      { code: 'edit', name: '编辑' },
      { code: 'resetPassword', name: '重置密码' },
      { code: 'enable', name: '启用/停用' },
    ]),
    createPermissionNode('/accountCenter/role', '角色权限管理', 'PERMISSION_ACCOUNT_ROLE', [
      { code: 'detail', name: '详情' },
      { code: 'edit', name: '编辑' },
      { code: 'delete', name: '删除' },
      { code: 'enable', name: '启用/停用' },
    ]),
  ]),
];

export const mapFrontTreeToFunctionList = (
  tree: IFrontPermissionNode[] = FrontPermissionTree,
): FunctionListModule[] => tree.map((module) => ({
  path: module.frontPath,
  children: module.children.map((child) => ({
    path: child.frontPath,
    permissionList: child.permissionList.map((permission) => ({ code: permission.frontCode })),
  })),
}));

const universalLayoutRotes: IRouter[] = [
  {
    path: '/home',
    meta: { icon: 'home', title: '首页', keepAlive: false },
    component: lazy(() => import('@/pages/GeneralTradeLogistics/Cockpit')),
  },
  {
    path: '/cockpit',
    meta: { hidden: true, keepAlive: false, title: '首页' },
    component: lazy(() => import('@/pages/GeneralTradeLogistics/Cockpit')),
  },
  {
    path: '/cockpit/overview',
    meta: { hidden: true, keepAlive: false, title: '首页' },
    component: lazy(() => import('@/pages/GeneralTradeLogistics/Cockpit')),
  },
  {
    path: '/generalTradeLogisticsFulfillment',
    meta: { icon: 'control', title: '头程履约', menuGroup: true },
    redirect: '/generalTradeLogistics/fulfillmentWorkbench',
    children: [
      { path: '/generalTradeLogistics/fulfillmentWorkbench', meta: { keepAlive: false, title: '履约工作台' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/FulfillmentWorkbench')) },
      { path: '/generalTradeLogistics/fulfillmentWorkbench/detail', meta: { hidden: true, keepAlive: false, title: '托书详情', selectLeftMenu: '/generalTradeLogistics/fulfillmentWorkbench', tabNavType: 'querypath' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/FulfillmentWorkbench/BookingDetail')) },
    ],
  },
  {
    path: '/generalTradeLogistics',
    meta: { icon: 'control', title: '头程竞价管理' },
    redirect: '/generalTradeLogistics/freightInquiry',
    children: [
      { path: '/generalTradeLogistics/freightInquiry', meta: { keepAlive: false, title: '发起询价' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/FreightInquiry')) },
      { path: '/generalTradeLogistics/inquiryManagement', meta: { keepAlive: false, title: '询价管理' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/InquiryManagement')) },
      { path: '/generalTradeLogistics/competitionAnalysis', meta: { keepAlive: false, title: '竞价分析' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/CompetitionAnalysis')) },
      { path: '/generalTradeLogistics/pricingReview', meta: { keepAlive: false, title: '定价审核' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/PricingReview')) },
      { path: '/generalTradeLogistics/logisticsTracking', meta: { keepAlive: false, title: '物流轨迹跟踪' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/LogisticsTracking')) },
      { path: '/generalTradeLogistics/manualTracking', meta: { keepAlive: false, title: '头程物流录入' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/ManualTracking')) },
      { path: '/generalTradeLogistics/supplytrack', meta: { keepAlive: false, title: '第三方物流轨迹查询' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/Supplytrack')) },
      { path: '/generalTradeLogistics/reconciliationReview', meta: { keepAlive: false, title: '对账与审核' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/FeaturePlaceholder')) },
      { path: '/generalTradeLogistics/exceptionPermission', meta: { keepAlive: false, title: '异常与权限' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/FeaturePlaceholder')) },
    ],
  },
  {
    path: '/generalTradeLogisticsOrders',
    meta: { icon: 'list', menuGroup: true, title: 'AI财务' },
    redirect: '/generalTradeLogistics/salesOrders',
    children: [
      { path: '/generalTradeLogistics/salesOrders', meta: { keepAlive: false, title: '单据池' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/SalesOrders')) },
    ],
  },
  {
    path: '/generalTradeLogisticsBase',
    meta: { icon: 'set', title: '基础配置' },
    redirect: '/generalTradeLogisticsBase/providerConfig',
    children: [
      { path: '/generalTradeLogisticsBase/providerConfig', meta: { keepAlive: false, title: '物流商参数配置' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/ProviderConfig')) },
      { path: '/generalTradeLogisticsBase/systemConfig', meta: { keepAlive: false, title: '系统参数配置' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/SystemConfig')) },
      { path: '/generalTradeLogisticsBase/financeFoundation', meta: { keepAlive: false, title: '财务基础配置' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/FinanceFoundation')) },
      { path: '/generalTradeLogisticsBase/lingxingProducts', meta: { keepAlive: false, title: '领星产品' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/LingxingProducts')) },
    ],
  },
  {
    path: '/accountCenter',
    meta: { icon: 'user', title: '用户权限管理' },
    redirect: '/accountCenter/account',
    children: [
      { path: '/accountCenter/account', meta: { keepAlive: false, title: '后台用户管理' }, component: lazy(() => import('@/pages/accountCenter/account')) },
      { path: '/accountCenter/role', meta: { keepAlive: false, title: '角色权限管理' }, component: lazy(() => import('@/pages/accountCenter/role')) },
    ],
  },
  { path: '/accountCenter/account/edit', meta: { hidden: true, keepAlive: false, title: '编辑用户' }, component: lazy(() => import('@/pages/accountCenter/account/edit')) },
  { path: '/accountCenter/account/detail', meta: { hidden: true, keepAlive: false, title: '用户详情' }, component: lazy(() => import('@/pages/accountCenter/account/detail')) },
  { path: '/accountCenter/role/edit', meta: { hidden: true, keepAlive: false, title: '编辑角色' }, component: lazy(() => import('@/pages/accountCenter/role/edit')) },
  { path: '/accountCenter/role/detail', meta: { hidden: true, keepAlive: false, title: '角色详情' }, component: lazy(() => import('@/pages/accountCenter/role/detail')) },
  { path: '/generalTradeLogistics/purchaseOrders', meta: { hidden: true, keepAlive: false, title: '业务单据' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/SalesOrders')) },
  { path: '/aiFinance/receivables', meta: { hidden: true, keepAlive: false, title: '收款池' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/Receivables')) },
  { path: '/aiFinance/receivables/detail', meta: { hidden: true, keepAlive: false, title: '应收单详情' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/Receivables/Detail')) },
  { path: '/aiFinance/receivables/platform-bill-detail', meta: { hidden: true, keepAlive: false, title: '平台账单核对' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/Receivables/PlatformBillDetail')) },
  { path: '/aiFinance/payables', meta: { hidden: true, keepAlive: false, title: '请款池' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/PaymentPool')) },
  { path: '/aiFinance/payables/expense-detail', meta: { hidden: true, keepAlive: false, title: '费用单详情' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/PurchasePayables/ExpenseSourceDetail')) },
  { path: '/aiFinance/payables/detail', meta: { hidden: true, keepAlive: false, title: '应付单详情' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/PaymentPool')) },
  { path: '/aiFinance/payables/request-detail', meta: { hidden: true, keepAlive: false, title: '请款单详情' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/PaymentPool')) },
  { path: '/aiFinance/payables/bill-detail', meta: { hidden: true, keepAlive: false, title: '供应商对账单核对' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/FinanceBills')) },
  { path: '/aiFinance/funds', meta: { hidden: true, keepAlive: false, title: '资金池' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/Funds')) },
  { path: '/aiFinance/settings', meta: { hidden: true, keepAlive: false, title: '设置' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/FinanceSettings')) },
  { path: '/generalTradeLogistics/salesOrders/detail', meta: { hidden: true, keepAlive: false, title: 'SO 销售单详情' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/SalesOrders/Detail')) },
  { path: '/generalTradeLogistics/salesOrders/fo-detail', meta: { hidden: true, keepAlive: false, title: 'FO 履约单详情' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/FulfillmentOrders/Detail')) },
  { path: '/generalTradeLogistics/salesOrders/to-detail', meta: { hidden: true, keepAlive: false, title: 'TO 头程物流单详情' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/TransportOrders/Detail')) },
  { path: '/generalTradeLogistics/salesOrders/po-detail', meta: { hidden: true, keepAlive: false, title: 'PO 采购单详情' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/PurchaseOrders/Detail')) },
  { path: '/generalTradeLogistics/providerConfig', meta: { hidden: true, keepAlive: false, title: '物流商参数配置' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/ProviderConfig')) },
  { path: '/generalTradeLogistics/systemConfig', meta: { hidden: true, keepAlive: false, title: '系统参数配置' }, component: lazy(() => import('@/pages/GeneralTradeLogistics/SystemConfig')) },
];

export default universalLayoutRotes;
export { PARENT_SECONDARY_MAP, FrontPermissionTree };
